import { DatePipe } from '@angular/common';
import { CostBasisMethods } from '@interfaces/models/cost-basis-methods';
import { SpecIDFallback } from '@interfaces/models/spec-id-fallback.enum';
import {
  AccountTypeNames,
  AccountTypes,
  IReceipt,
  ISelectedAccount,
  TooltipReceipt,
} from '@shared/models';
import { PageContent } from '@src/app/secure/review/containers/gifting-review/gifting-review-content-mapper';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import {
  getIsBrokerageFirmRecipient,
  getIsCharityRecipient,
  getIsTransferAgentRecipient,
  getRecipientAccountNumber,
  getRecipientAnonymousGift,
  getRecipientCity,
  getRecipientDTCNumber,
  getRecipientFirm,
  getRecipientName,
  getRecipientSSN,
  getRecipientState,
  getRecipientStreetAddress,
  getRecipientTransferAgentName,
  getRecipientZipcode,
  GiftingRecipient,
  GiftingState,
} from '@store/reducers/gifting/gifting.reducer';
import {
  getAccountTypeSelection,
  getIsGiftingFlow,
  getIsVanguardGiftingFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { GiftingMethodEnum } from 'src/app/secure/gifting-method/gifting-position.constants';

import { ReviewContent } from '../../../../../shared/services/content/content-jsons/review-content';
import { toSentenceLowerCase } from '../cashier-nonretirement';
import { formatProductTypeName } from '../internal-transfer/cashier-internal-transfer';
const datePipe = new DatePipe('en-US');

const getAccountTypeName = (accountType: AccountTypes): AccountTypeNames =>
  AccountTypeNames[accountType];

export function buildAccountFromGifting(
  triageState: TriageState,
  accountFrom: ISelectedAccount,
  editable: boolean,
  remoteContent?: PageContent,
): IReceipt {
  const accountTypeName = getAccountTypeName(getAccountTypeSelection(triageState));
  const receipt: IReceipt = {
    header: remoteContent?.transferringFromHeading ?? 'Transferring from',
    editable: editable,
    editRoute: 'secure/gifting/assets',
    sectionEvidenceID: 'accountFrom',
    rows: [
      {
        label: remoteContent?.accountTypeLabel ?? 'Account type',
        values: [
          `${accountTypeName} ${formatProductTypeName(accountFrom?.accountData?.productType)} ${remoteContent?.accountLabel ?? 'account'}`,
        ],
        evidenceID: 'fromAccountType',
      },
      {
        label: remoteContent?.accountNumberLabel ?? 'Account number',
        values: [`${accountFrom?.accountNumber}`],
        evidenceID: 'fromAccountNumber',
      },
    ],
  };

  if (accountFrom?.accountData?.nickname) {
    receipt.rows.push({
      label: 'Account nickname',
      values: [accountFrom.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  return receipt;
}

const costBasisDictionary = {
  SpecID: (remoteContent?: PageContent) =>
    remoteContent?.specIdLabel ?? 'Specific identification (SpecID)',
  FIFO: (remoteContent?: PageContent) => remoteContent?.fifoLabel ?? 'First in, first out (FIFO)',
  HIFO: (remoteContent?: PageContent) => remoteContent?.hifoLabel ?? 'Highest in, first out (HIFO)',
  AvgCost: (remoteContent?: PageContent) => remoteContent?.avgCostLabel ?? 'Average cost (AvgCost)',
  MinTax: (remoteContent?: PageContent) => remoteContent?.minTaxLabel ?? 'Minimum Tax (MinTax)',
};

const getMailingAddress = (recipient: GiftingRecipient) =>
  getRecipientStreetAddress(recipient) +
  '<br>' +
  getRecipientCity(recipient) +
  ', ' +
  getRecipientState(recipient) +
  ' ' +
  getRecipientZipcode(recipient);

const getFormattedRecipientDateOfBirth = (recipient: GiftingRecipient) => {
  return datePipe.transform(recipient.dateOfBirth, 'MMMM d, y');
};

function getShareDollarAmountForPosition(
  position: CostBasisPosition,
  isVanguardGifting: boolean,
): string {
  if (position.selectedCostBasisMethod === CostBasisMethods.SPECID)
    return `${position.taxLotsTotalShares} share${+position.taxLotsTotalShares === 1 ? '' : 's'} | ${
      position.taxLotsTotalMarketValue
    }${isVanguardGifting ? '*' : ''}`;
  if (position.giftingMethod === 'DOLLAR_AMOUNT') return String(position.selectedAmount);
  return `${position.selectedAmount} share${+position.selectedAmount === 1 ? '' : 's'} | ${
    position.sharesTotalMarketValue
  }${isVanguardGifting ? '*' : ''}`;
}

export function buildGiftReceipt(
  selectedPositions: CostBasisPosition[],
  triageState: TriageState,
  giftingState: GiftingState,
  editable: boolean,
  remoteContent?: PageContent,
): IReceipt {
  const receipt: IReceipt = {
    header: remoteContent?.giftHeading ?? 'Gift',
    editable: editable,
    editRoute: 'secure/gifting/assets',
    sectionEvidenceID: 'gift',
    rows: [],
  };
  const isVanguardGifting = getIsVanguardGiftingFlow(triageState);

  selectedPositions.forEach((position) => {
    const positionHeader = getPositionHeader(position);

    receipt.rows.push(
      {
        label: '',
        values: [positionHeader],
        evidenceID: 'positionName',
        hideBorder: true,
        customLineHeight: true,
      },
      {
        label: 'Gift method',
        values: [toSentenceLowerCase(position.giftingMethod).replace('_', ' ')],
        evidenceID: 'giftMethod',
        hideBorder: true,
        customLineHeight: true,
      },
    );

    if (!(position.settlementFund || !position.selectedCostBasisMethod)) {
      receipt.rows.push({
        label: remoteContent?.costBasisMethodLabel ?? 'Cost basis method',
        values: [costBasisDictionary[position.selectedCostBasisMethod](remoteContent)],
        evidenceID: 'costBasisMethod',
        hideBorder: true,
        customLineHeight: true,
      });
    }

    receipt.rows.push({
      label: ReviewContent.sharesDollarAmountLabel,
      values: [getShareDollarAmountForPosition(position, isVanguardGifting)],
      evidenceID: 'shareDollarAmount',
      hideBorder: true,
      customLineHeight: true,
    });

    position.taxLots?.forEach((lot) => {
      if (lot.quantity) {
        receipt.rows.push(
          {
            label: remoteContent?.dateAcquiredLabel ?? 'Date acquired',
            values: [lot.aquiredDate],
            evidenceID: 'aquiredDate',
            hideBorder: true,
            customLineHeight: true,
          },
          {
            label: remoteContent?.sharesLabel ?? 'Shares',
            values: [lot.quantity.toFixed(3)],
            evidenceID: 'lotShares',
            hideBorder: true,
            customLineHeight: true,
          },
          {
            label: 'Lot Id',
            values: [lot.lotID],
            evidenceID: 'lotId',
            hideRow: true,
          },
          {
            label: 'Cost per share',
            values: [lot.costperShare?.toString() ?? ''],
            evidenceID: 'costPerShare',
            hideRow: true,
          },
        );
      }
    });

    receipt.rows.push({
      label: '',
      values: [''],
      evidenceID: '',
      customLineHeight: true,
    });
  });

  receipt.rows.push({
    label: remoteContent?.estTotalGiftValueLabel ?? 'Est. total gift value as of',
    values: [giftingState.totalGiftAmount + (isVanguardGifting ? '*' : '')],
    evidenceID: 'totalGift',
    isLabelBold: true,
  });
  return receipt;
}

export function buildAccountToGifting(
  recipient: GiftingRecipient,
  triageState: TriageState,
  editable: boolean,
  remoteContent?: PageContent,
): IReceipt {
  const accountToHeader = {
    header: remoteContent?.transferringToHeading ?? 'Transferring to',
    editable: editable,
    editRoute: 'secure/gifting/recipient-triage',
    sectionEvidenceID: 'accountTo',
  };
  switch (true) {
    case getIsVanguardGiftingFlow(triageState):
      return {
        ...accountToHeader,
        rows: [
          {
            label: remoteContent?.giftRecipientLabel ?? 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: remoteContent?.receivingAccountNumberLabel ?? 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsCharityRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: remoteContent?.nameOfReceivingFirmLabel ?? 'Name of receiving firm',
            values: [getRecipientFirm(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: remoteContent?.anonymouslyGiftingLabel ?? 'Anonymously gifted',
            values: [getRecipientAnonymousGift(recipient)],
            evidenceID: 'toAccountAnonymousGift',
          },
          {
            label: remoteContent?.dtcNumberLabel ?? 'DTC number',
            values: [getRecipientDTCNumber(recipient)],
            evidenceID: 'toAccountDTCNumber',
          },
          {
            label: remoteContent?.giftRecipientLabel ?? 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: remoteContent?.receivingAccountNumberLabel ?? 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsBrokerageFirmRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: remoteContent?.nameOfReceivingFirmLabel ?? 'Name of receiving firm',
            values: [getRecipientFirm(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: remoteContent?.dtcNumberLabel ?? 'DTC number',
            values: [getRecipientDTCNumber(recipient)],
            evidenceID: 'toAccountDTCNumber',
          },
          {
            label: remoteContent?.giftRecipientLabel ?? 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: remoteContent?.receivingAccountNumberLabel ?? 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
    case getIsTransferAgentRecipient(recipient):
      return {
        ...accountToHeader,
        rows: [
          {
            label: remoteContent?.nameOfTransferAgentLabel ?? 'Name of transfer agent',
            values: [getRecipientTransferAgentName(recipient)],
            evidenceID: 'toAccountTransferAgentName',
          },
          {
            label:
              remoteContent?.nameOfIndividualTrustOrOrgLabel ??
              'Name of individual trust or organization',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountFirm',
          },
          {
            label: remoteContent?.receivingAccountNumberLabel ?? 'Receiving account number',
            values: [getRecipientAccountNumber(recipient) || 'N/A'],
            evidenceID: 'toAccountNumber',
          },
          {
            label: remoteContent?.mailingAddressLabel ?? 'Mailing address',
            values: [getMailingAddress(recipient)],
            evidenceID: 'toAccountMailingAddress',
          },
          {
            label: 'Social Security Number',
            values: [getRecipientSSN(recipient)],
            evidenceID: 'toAccountSNN',
          },
          {
            label: remoteContent?.dateOfBirthLabel ?? 'Date of birth',
            values: [getFormattedRecipientDateOfBirth(recipient) || 'N/A'],
            evidenceID: 'toAccountDateOfBirth',
          },
        ],
      };
    default:
      return {
        ...accountToHeader,
        rows: [
          {
            label: remoteContent?.giftRecipientLabel ?? 'Gift recipient',
            values: [getRecipientName(recipient)],
            evidenceID: 'toAccountRegistration',
          },
          {
            label: remoteContent?.receivingAccountNumberLabel ?? 'Receiving account number',
            values: [getRecipientAccountNumber(recipient)],
            evidenceID: 'toAccountNumber',
          },
        ],
      };
  }
}

export function buildSpecIDFallBackMethodReceipt(
  backupCostBasisMethod: SpecIDFallback,
  editable: boolean,
): IReceipt {
  const specIDFallbackHeader = {
    header: 'Back up cost basis method',
    editable: editable,
    editRoute: 'secure/gifting/',
    sectionEvidenceID: 'specIdFallback',
  };
  switch (true) {
    case backupCostBasisMethod === SpecIDFallback.FIFO:
      return {
        ...specIDFallbackHeader,
        rows: [
          {
            label: '',
            values: ['Continue with FIFO'],
            evidenceID: 'positionName',
            customLineHeight: true,
            hideBorder: true,
            isLabelBold: true,
          },
          {
            label: `I allow Vanguard to change my cost basis method into FIFO in the event the lots I chose to gift are no longer available at the time the gift is processed. `,
            isLabelBold: false,
            customLineHeight: false,
            values: [],
            evidenceID: 'specIdFallbackExplainer',
          },
          {
            label: '',
            values: [''],
            evidenceID: '',
            customLineHeight: true,
          },
        ],
      };
    case backupCostBasisMethod === SpecIDFallback.CANCEL:
      return {
        ...specIDFallbackHeader,
        rows: [
          {
            label: '',
            values: ['I’d prefer to cancel the request'],
            evidenceID: 'positionName',
            customLineHeight: true,
            hideBorder: true,
            isLabelBold: true,
          },
          {
            label: `Please cancel my gift if it can't be processed with the specific lots I requested.`,
            isLabelBold: false,
            customLineHeight: false,
            values: [],
            evidenceID: 'specIdFallbackExplainer',
          },
          {
            label: '',
            values: [''],
            evidenceID: '',
            customLineHeight: true,
          },
        ],
      };
    default:
      return null;
  }
}

function getPositionHeader(position: CostBasisPosition) {
  return position.ticker && position.securityName
    ? `${position.ticker} &mdash; ${position.securityName}`
    : position.ticker || position.securityName;
}

export function buildAdditionalTaxLot(
  selectedPositions: CostBasisPosition[],
  triageState: TriageState,
  editable: boolean,
  remoteContent?: PageContent,
): TooltipReceipt {
  const isGifting = getIsGiftingFlow(triageState);

  if (!isGifting || selectedPositions.length === 0) return null;

  const receipt: TooltipReceipt = {
    header: 'Backup tax lot for fractional shares',
    editable: editable,
    editRoute: 'secure/gifting/method/1',
    sectionEvidenceID: 'additionalTaxLot',
    toolTip:
      'We will use the chosen lot for any additional fractional shares needed for your gift.',
    rows: [],
  };
  const isVanguardGifting = getIsVanguardGiftingFlow(triageState);

  selectedPositions.forEach((position) => {
    const positionHeader = getPositionHeader(position);
    const lot = position.additionalTaxLot;
    const isEligible = isDollarAmountSpecIdFlow(position);
    if (isEligible && lot) {
      const modifiedPosition: CostBasisPosition = {
        ...position,
        taxLotsTotalShares: '' + lot.quantity,
        taxLotsTotalMarketValue: lot.formattedMarketValue,
      };
      receipt.rows.push(
        {
          label: '',
          values: [positionHeader],
          evidenceID: 'positionName',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: ReviewContent.sharesDollarAmountLabel,
          values: [getShareDollarAmountForPosition(modifiedPosition, isVanguardGifting)],
          evidenceID: 'shareDollarAmount',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: remoteContent?.dateAcquiredLabel ?? 'Date acquired',
          values: [lot.aquiredDate],
          evidenceID: 'aquiredDate',
          hideBorder: true,
          customLineHeight: true,
        },
        {
          label: 'Lot Id',
          values: [lot.lotID],
          evidenceID: 'lotId',
          hideRow: true,
        },
        {
          label: 'Cost per share',
          values: [lot.costperShare?.toString() ?? ''],
          evidenceID: 'costPerShare',
          hideRow: true,
        },
        {
          label: '',
          values: [''],
          evidenceID: '',
          customLineHeight: true,
        },
      );
    }
  });
  if (receipt.rows.length === 0) return null;

  return receipt;
}

export const isDollarAmountSpecIdFlow = (position: CostBasisPosition) => {
  return (
    position.giftingMethod === GiftingMethodEnum.DOLLAR_AMOUNT &&
    position.selectedCostBasisMethod === CostBasisMethods.SPECID
  );
};
